import { useCallback, useEffect, useRef, useState } from "react";

import { api, type SellSheetIndex, type SellSheetRow } from "../api/client";
import { formatApiError } from "../lib/api-errors";
import { downloadBlobResponse, openBlobResponse } from "../lib/blob-download";
import { formatTs } from "../lib/format-ts";
import { sellSheetFilename } from "../../worker/sell-sheet/spec";
import { TopBar } from "../components/layout/TopBar";
import { Button } from "../components/shared/Button";
import { EmptyState } from "../components/shared/EmptyState";
import { Spinner } from "../components/shared/Spinner";
import * as ui from "../styles/ui";

/** Generated sheets, newest first, each openable or downloadable as a PDF. */
function HistoryTable({
  sheets,
  busyId,
  onOpen,
}: {
  sheets: SellSheetRow[];
  busyId: string | null;
  onOpen: (sheet: SellSheetRow, download: boolean) => void;
}) {
  return (
    <table style={ui.table}>
      <thead>
        <tr>
          <th style={{ ...ui.thFirst, width: 150 }}>generated</th>
          <th style={{ ...ui.th, width: 90 }}>ticker</th>
          <th style={ui.th}>index</th>
          <th style={{ ...ui.th, width: 80 }}>level</th>
          <th style={{ ...ui.th, width: 110 }}>as of</th>
          <th style={{ ...ui.th, width: 150 }}>by</th>
          <th style={{ ...ui.th, width: 150 }}>pdf</th>
        </tr>
      </thead>
      <tbody>
        {sheets.map((s) => (
          <tr key={s.id} style={ui.row}>
            <td style={styles.tdMono}>{formatTs(s.createdAt)}</td>
            <td style={styles.tdMono}>{s.ticker}</td>
            <td style={styles.td}>{s.name}</td>
            <td style={styles.tdNum}>
              {s.level == null ? "-" : s.level.toFixed(2)}
            </td>
            <td style={styles.td}>{s.asOf}</td>
            <td style={styles.tdMono}>{s.generatedBy}</td>
            <td style={styles.td}>
              <span style={styles.actions}>
                <Button
                  variant="outline"
                  disabled={busyId === s.id}
                  onClick={() => onOpen(s, false)}
                >
                  {busyId === s.id ? "opening…" : "view"}
                </Button>
                <Button
                  variant="outline"
                  disabled={busyId === s.id}
                  onClick={() => onOpen(s, true)}
                >
                  download
                </Button>
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function SellSheetsPage() {
  const [indices, setIndices] = useState<SellSheetIndex[]>([]);
  const [selected, setSelected] = useState("");
  const [sheets, setSheets] = useState<SellSheetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A new tab keeps reading from its object URL for as long as it is open, so
  // those are held here and revoked together on unmount. The download path
  // revokes its own immediately.
  const objectUrls = useRef<string[]>([]);
  useEffect(
    () => () => {
      for (const url of objectUrls.current) URL.revokeObjectURL(url);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.sellSheets.indices(), api.sellSheets.list()])
      .then(([a, b]) => {
        if (cancelled) return;
        setIndices(a.indices);
        setSelected((cur) => cur || a.indices[0]?.id || "");
        setSheets(b.sheets);
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(formatApiError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const generate = useCallback(async () => {
    if (!selected) return;
    setGenerating(true);
    setError(null);
    try {
      const { sheet } = await api.sellSheets.generate(selected);
      setSheets((cur) => [sheet, ...cur]);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  }, [selected]);

  const openPdf = useCallback(
    async (sheet: SellSheetRow, download: boolean) => {
      setBusyId(sheet.id);
      setError(null);
      try {
        const res = await api.sellSheets.pdf(sheet.id, download);
        if (download) {
          const name = `${sellSheetFilename(sheet.ticker, sheet.asOf)}.pdf`;
          await downloadBlobResponse(res, name);
        } else {
          objectUrls.current.push(await openBlobResponse(res));
        }
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setBusyId(null);
      }
    },
    [],
  );

  return (
    <>
      <TopBar title="sell sheets" />
      <div style={ui.pageContent}>
        {loading ? (
          <Spinner size={22} label="loading…" />
        ) : (
          <>
            <div style={styles.bar}>
              <label style={styles.label} htmlFor="sell-sheet-index">
                index
              </label>
              <select
                id="sell-sheet-index"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                style={styles.select}
              >
                {indices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.ticker} {i.name}
                    {i.halted ? " (halted)" : ""}
                  </option>
                ))}
              </select>
              <Button
                variant="primary"
                onClick={generate}
                disabled={generating || !selected}
              >
                {generating ? "generating…" : "generate"}
              </Button>
              <span style={ui.metaText}>
                reads the adjacent public api, delayed 15 minutes
              </span>
            </div>

            {error && <p style={styles.error}>{error}</p>}

            {sheets.length === 0 ? (
              <EmptyState title="no sell sheets yet" />
            ) : (
              <HistoryTable sheets={sheets} busyId={busyId} onOpen={openPdf} />
            )}
          </>
        )}
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  bar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  label: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-3)",
    textTransform: "lowercase",
  },
  select: {
    padding: "4px 8px",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--fg-1)",
    background: "transparent",
    border: "1px solid var(--comp-rule)",
    borderRadius: 0,
    minWidth: 320,
  },
  actions: { display: "flex", gap: 6 },
  error: ui.errorText,
  td: ui.tdMuted,
  tdMono: ui.tdMono,
  tdNum: {
    ...ui.tdMono,
    color: "var(--fg-1)",
    fontVariantNumeric: "tabular-nums",
  },
};
